import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    this.updateExtraDiscountBar(newProductPrice, event.detail.resource);

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Updates the extra discount bar
   * @param {Element} newProductPrice - The product-price element from the updated HTML.
   * @param {VariantUpdateEvent['detail']['resource']} variant - The selected variant.
   */
  updateExtraDiscountBar(newProductPrice, variant) {
    const extraDiscountBar = this.querySelector('[data-extra-discount-bar]');
    const newExtraDiscountBar = newProductPrice.querySelector('[data-extra-discount-bar]');

    if (!newExtraDiscountBar) {
      extraDiscountBar?.remove();
      return;
    }

    if (extraDiscountBar) {
      extraDiscountBar.replaceWith(newExtraDiscountBar);
    } else {
      this.appendChild(newExtraDiscountBar);
    }

    this.updateExtraDiscountPrice(variant);
  }

  /**
   * Updates the extra discount price when the selected variant changes.
   * @param {VariantUpdateEvent['detail']['resource']} variant - The selected variant.
   */
  updateExtraDiscountPrice(variant) {
    const extraDiscountPrice = /** @type {HTMLElement | null} */ (this.querySelector('[data-extra-discount-price]'));
    const variantPrice = Number(variant?.price);

    if (!extraDiscountPrice || Number.isNaN(variantPrice)) {
      return;
    }

    let extraDiscountPercent = parseFloat(extraDiscountPrice.dataset.extraDiscountPercent ?? '0');

    if (Number.isNaN(extraDiscountPercent)) {
      extraDiscountPercent = 0;
    }

    extraDiscountPercent = Math.max(0, Math.min(extraDiscountPercent, 100));

    const extraDiscountMultiplier = (100 - extraDiscountPercent) / 100;
    const discountedPrice = Math.round(variantPrice * extraDiscountMultiplier);
    const moneyFormat = extraDiscountPrice.dataset.moneyFormat || '{{amount}}';
    const currency = extraDiscountPrice.dataset.currency || 'USD';

    extraDiscountPrice.innerHTML = formatMoney(discountedPrice, moneyFormat, currency);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
